import { useState } from 'react';
import type React from 'react';
import { createFileRoute, Outlet, useNavigate } from '@tanstack/react-router';
import { ClassList } from '../app/components/class/class-list';
import { ProtectedRoute, Button, Input, Label, GenericModal, ConfirmDialog, useAuthorization } from '@org/ui';
import { trpc } from '@org/clients/school';
import type { ClassSelect } from '@org/model';
import { useTranslation } from 'react-i18next';

export const Route = createFileRoute('/classes')({
  component: ClassesRoute,
});

function ClassesRoute() {
  const navigate = useNavigate({ from: '/classes' });
  const { hasPermission } = useAuthorization();
  const { t } = useTranslation('admin');
  const canDelete = hasPermission('classes:delete');
  const utils = trpc.useUtils();
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [docId, setDocId] = useState('');
  const [existingClassId, setExistingClassId] = useState('');
  const [unitId, setUnitId] = useState('');
  const [importError, setImportError] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<
    { kind: 'delete' | 'duplicate'; classItem: ClassSelect } | null
  >(null);

  const globalWindow =
    typeof globalThis !== 'undefined' && (globalThis as any).window
      ? (globalThis as any).window
      : undefined;

  const importMutation = trpc.classes.importFromGoogleDoc.useMutation({
    onSuccess: async (data) => {
      await utils.classes.list.invalidate();
      setImportModalOpen(false);
      setDocId('');
      setExistingClassId('');
      setUnitId('');
      setImportError(null);

      const jobNotice = data?.jobId
        ? `Queued class import (job ${data.jobId}). Refresh later to see results.`
        : 'Queued class import request.';
      globalWindow?.alert?.(jobNotice);
      if (data?.classId) {
        navigate({ to: '/classes/$classId', params: { classId: data.classId } });
      }
    },
    onError: (error) => {
      setImportError(error.message);
    },
  });
  const isImporting =
    (importMutation as any).isLoading ?? importMutation.status === 'pending';

  const deleteMutation = trpc.classes.deleteClass.useMutation({
    onSuccess: async () => {
      await utils.classes.list.invalidate();
    },
  });

  const [duplicatingClassId, setDuplicatingClassId] = useState<string | null>(null);

  const duplicateMutation = trpc.classes.duplicateClass.useMutation({
    onSuccess: async (data) => {
      await utils.classes.list.invalidate();
      setDuplicatingClassId(null);
      navigate({ to: '/classes/$classId', params: { classId: data.classId } });
    },
    onError: (error) => {
      setDuplicatingClassId(null);
      globalWindow?.alert?.(`Failed to duplicate class: ${error.message}`);
    },
  });
  const isDuplicating =
    (duplicateMutation as any).isLoading ?? duplicateMutation.status === 'pending';

  const handleImport = async () => {
    setImportError(null);
    try {
      await importMutation.mutateAsync({
        docId: docId.trim(),
        classId: existingClassId.trim() || undefined,
        unitId: unitId.trim() || undefined,
      });
    } catch (error) {
      // handled in onError
    }
  };

  const handleDeleteClass = (classItem: ClassSelect) => {
    setPendingAction({ kind: 'delete', classItem });
  };

  const handleDuplicateClass = (classItem: ClassSelect) => {
    setPendingAction({ kind: 'duplicate', classItem });
  };

  const runPendingAction = async () => {
    if (!pendingAction) return;
    const { kind, classItem } = pendingAction;
    setPendingAction(null);
    if (kind === 'delete') {
      try {
        await deleteMutation.mutateAsync({ classId: classItem.classId });
      } catch (error) {
        console.error(error);
      }
      return;
    }
    setDuplicatingClassId(classItem.classId);
    try {
      await duplicateMutation.mutateAsync({ classId: classItem.classId });
    } catch (error) {
      // handled in onError
    }
  };

  const pendingClassName = pendingAction?.classItem.name ?? t('classes.list.untitled');

  return (
    <ProtectedRoute requiredPermissions={['admin:all', 'classes:*', 'classes:write']}>
      <div className="min-w-0 space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-semibold tracking-tight" data-testid="classes-heading">{t('sidebar.navigation.classes')}</h1>
          <Button variant="outline" onClick={() => setImportModalOpen(true)}>Import Google Doc</Button>
        </div>
        <ClassList
          onCreateClass={() => navigate({ to: '/classes/new' })}
          onEditClass={(classItem) =>
            navigate({ to: '/classes/$classId', params: { classId: classItem.classId } })
          }
          onDeleteClass={canDelete ? handleDeleteClass : undefined}
          onDuplicateClass={handleDuplicateClass}
          p2eBaseUrl={import.meta.env.VITE_P2E_URL}
        />
        <Outlet />
      </div>
        <GenericModal
          show={importModalOpen}
          onClose={() => {
            setImportModalOpen(false);
            setImportError(null);
          }}
          title="Import Class From Google Doc"
          body={
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="docId">Google Doc ID</Label>
                <Input
                  id="docId"
                  placeholder="1A2b3C..."
                  value={docId}
                  onChange={(event) => {
                    const nextValue = (event as any)?.target?.value ?? "";
                    setDocId(nextValue);
                  }}
                  disabled={isImporting}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="existingClassId">Existing Class ID (optional)</Label>
                <Input
                  id="existingClassId"
                  placeholder="Use to overwrite an existing class"
                  value={existingClassId}
                  onChange={(event) => {
                    const nextValue = (event as any)?.target?.value ?? "";
                    setExistingClassId(nextValue);
                  }}
                  disabled={isImporting}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="unitId">Unit ID (optional)</Label>
                <Input
                  id="unitId"
                  placeholder="Attach new class to a unit"
                  value={unitId}
                  onChange={(event) => {
                    const nextValue = (event as any)?.target?.value ?? "";
                    setUnitId(nextValue);
                  }}
                  disabled={isImporting}
                />
              </div>
              {importError && <p className="text-sm text-destructive">{importError}</p>}
            </div>
          }
          footer={
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setImportModalOpen(false)} disabled={isImporting}>
                Cancel
              </Button>
              <Button variant="outline" onClick={handleImport} disabled={!docId.trim() || isImporting}>
                {isImporting ? 'Importing...' : 'Import'}
              </Button>
            </div>
          }
        />
        <ConfirmDialog
          open={pendingAction !== null}
          onOpenChange={(open) => {
            if (!open) setPendingAction(null);
          }}
          description={
            pendingAction?.kind === 'delete'
              ? t('classes.list.deleteConfirm', { name: pendingClassName })
              : t('classes.list.duplicateConfirm', { name: pendingClassName })
          }
          confirmLabel={
            pendingAction?.kind === 'delete' ? t('classes.list.delete') : t('classes.list.duplicate')
          }
          destructive={pendingAction?.kind === 'delete'}
          onConfirm={runPendingAction}
        />
    </ProtectedRoute>
  );
}


